/* eslint-disable import/no-relative-packages -- the widget is a separate package with no workspace link, so the protocol it speaks is read in place */
import { useMutationState } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { AppWindow, MessageCircle } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import {
  type MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BackdropMotion,
  BackdropShape,
  drawnShapes,
  isBackdropMotion,
  isBackdropShape,
  supportsBackdropFilter,
} from '../../../../../widget/src/lib/backdropProtocol';
import { PreviewBackdropLayer } from './PreviewBackdropLayer';
import { usePreviewSupportIntent } from './usePreviewSupportIntent';
import { parseMetadataString, transformMetadataForWidget } from './widgetMetadata';
import { useBootstrapConfig } from '@/Domain/components/App/BootstrapProvider';
import { PreviewContext } from '@/Domain/components/PreviewProvider';
import {
  RadioGroupSegmented,
  RadioGroupSegmentedItem,
} from '@/Domain/components/Shared/RadioGroup/RadioGroup';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { cn } from '@/Domain/utils/cn';
import { getCurrentScenarioId } from '@/context';

const WIDGET_URL = import.meta.env.VITE_WIDGET_URL || 'https://bubble.quickchat.ai';
const WIDGET_ORIGIN = new URL(WIDGET_URL).origin;
// Matches the widget's own MOBILE_BREAKPOINT so isMobile is computed identically.
const MOBILE_BREAKPOINT = 768;

const TRANSITION_DURATION = 250;

type ViewMode = 'bubble' | 'embed';

type Backdrop = { blur: number; shapes: BackdropShape[] };

const VIEW_MODE_OPTIONS = [
  { value: 'bubble' as const, label: 'Widget', icon: MessageCircle },
  { value: 'embed' as const, label: 'Embed', icon: AppWindow },
];

type NewWidgetProps = {
  showViewModeToggle?: boolean;
  fixedViewType?: string;
  defaultViewMode?: ViewMode;
  className?: string;
  source?: string;
  customParams?: Record<string, unknown>;
  // Preview-only override forwarded to widget.html as the `colorScheme` URL
  // param. `system` (or undefined) leaves the host's prefers-color-scheme as
  // the source of truth.
  colorScheme?: 'system' | 'light' | 'dark';
  // Emulated host viewport forwarded as quickchat_viewport (like chat.js) for mobile vs desktop rendering.
  hostViewport?: { width: number; height: number };
  // Preview-only language override forwarded to widget.html as `?language=`.
  // Changing it remounts the iframe so the widget re-requests its (translated)
  // configuration in the new language.
  language?: string;
  // Embed view renders its own close button; posts quickchat_minimize, surfaced via onMinimize.
  showCloseButton?: boolean;
  onMinimize?: () => void;
  // Bumping this remounts the iframe, the preview's stand-in for a visitor reloading the page.
  reloadToken?: number;
  // Whether this instance owns PreviewContext.iframe (the Website live-edit push target).
  // Hidden (kept-alive AI Preview) and overlay instances pass false so they can't steal the shared ref.
  bindContextIframe?: boolean;
  // Hidden previews must not open Support AI for an unseen send.
  detectSupportIntent?: boolean;
  onSupportOpened?: () => void;
};

const NewWidget = ({
  showViewModeToggle = true,
  fixedViewType,
  defaultViewMode = 'bubble',
  className,
  source,
  customParams,
  colorScheme,
  hostViewport,
  language,
  showCloseButton,
  onMinimize,
  reloadToken = 0,
  bindContextIframe = true,
  detectSupportIntent = true,
  onSupportOpened,
}: NewWidgetProps) => {
  const [iframeKey, setIframeKey] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const postHog = usePostHog();

  const { apiServerUrl } = useBootstrapConfig();
  const host = `https://${apiServerUrl}`;
  const scenarioId = getCurrentScenarioId();

  const src = useMemo(() => {
    const params = new URLSearchParams({
      scenarioId,
      viewType: fixedViewType ?? viewMode,
      host,
      ...(source ? { source } : {}),
      ...(colorScheme && colorScheme !== 'system' ? { colorScheme } : {}),
      ...(language ? { language } : {}),
      ...(showCloseButton ? { showCloseButton: 'true' } : {}),
    });
    if (customParams && Object.keys(customParams).length > 0) {
      params.set('params', JSON.stringify(customParams));
    }
    return `${WIDGET_URL}/widget.html?${params.toString()}`;
  }, [
    scenarioId,
    fixedViewType,
    viewMode,
    host,
    source,
    colorScheme,
    language,
    showCloseButton,
    customParams,
  ]);

  const handleViewModeChange = (mode: ViewMode) => {
    if (mode === viewMode || isTransitioning) return;

    setIsTransitioning(true);

    setTimeout(() => {
      setViewMode(mode);
      setIframeKey((prev) => prev + 1);

      setTimeout(() => {
        setIsTransitioning(false);
      }, TRANSITION_DURATION);
    }, TRANSITION_DURATION);
  };

  // Keep a ref to handleViewModeChange to avoid stale closure in the effect below
  const handleViewModeChangeRef = useRef(handleViewModeChange);
  handleViewModeChangeRef.current = handleViewModeChange;

  const previewContext = useContext(PreviewContext);
  const localRef = useRef<HTMLIFrameElement>(null);
  const contextIframeRef = bindContextIframe ? previewContext?.iframe : undefined;

  // Callback ref that sets both the local ref (guaranteed to work) and the
  // context ref (used by setConfiguration in PreviewProvider).
  const mergedRef = useCallback(
    (node: HTMLIFrameElement | null) => {
      localRef.current = node;
      if (
        contextIframeRef &&
        typeof contextIframeRef === 'object' &&
        'current' in contextIframeRef
      ) {
        (contextIframeRef as MutableRefObject<HTMLIFrameElement | null>).current = node;
      }
    },
    [contextIframeRef],
  );

  const prevBubbleReqRef = useRef(0);
  useEffect(() => {
    const reqId = previewContext?.bubbleViewRequestId ?? 0;
    if (reqId > prevBubbleReqRef.current) {
      prevBubbleReqRef.current = reqId;
      handleViewModeChangeRef.current('bubble');
    }
  }, [previewContext?.bubbleViewRequestId]);

  // Failsafe refetch for non-context-owning instances only; the context-owning one is already refreshed by usePreview().refreshConfiguration(), so refreshing here too would double-refetch and flicker.
  const refreshConfiguration = useCallback(() => {
    localRef.current?.contentWindow?.postMessage(
      { type: 'quickchat_refresh_configuration' },
      WIDGET_ORIGIN,
    );
  }, []);

  const mutationSuccessCount = useMutationState({
    filters: { mutationKey: ['widget-settings'], status: 'success' },
    select: (mutation) => mutation.state.submittedAt,
  });

  useEffect(() => {
    if (bindContextIframe) return;
    if (mutationSuccessCount.length > 0) {
      refreshConfiguration();
    }
  }, [
    bindContextIframe,
    mutationSuccessCount.length,
    mutationSuccessCount.at(-1),
    refreshConfiguration,
  ]);

  // Forward the emulated host viewport so the widget picks mobile vs desktop (only read while iframed).
  useEffect(() => {
    if (!hostViewport) return;
    const send = () =>
      localRef.current?.contentWindow?.postMessage(
        {
          type: 'quickchat_viewport',
          data: {
            width: hostViewport.width,
            height: hostViewport.height,
            isMobile: hostViewport.width < MOBILE_BREAKPOINT,
          },
        },
        WIDGET_ORIGIN,
      );
    const onReady = (event: MessageEvent) => {
      if (
        event.source === localRef.current?.contentWindow &&
        event.data?.type === 'quickchat_ready'
      ) {
        send();
      }
    };
    window.addEventListener('message', onReady);
    send();
    return () => window.removeEventListener('message', onReady);
  }, [hostViewport?.width, hostViewport?.height]);

  // Surface the widget's quickchat_minimize so the host can close its container.
  useEffect(() => {
    if (!onMinimize) return;
    const handleMinimize = (event: MessageEvent) => {
      if (
        event.source === localRef.current?.contentWindow &&
        event.data?.type === 'quickchat_minimize'
      ) {
        onMinimize();
      }
    };
    window.addEventListener('message', handleMinimize);
    return () => window.removeEventListener('message', handleMinimize);
  }, [onMinimize]);

  // preview_message_sent: the widget posts quickchat_message_sent on each visitor send while in
  // preview (source=preview), so this fires only for the in-app AI Preview, never live traffic.
  useEffect(() => {
    const handlePreviewMessageSent = (event: MessageEvent) => {
      if (
        event.source === localRef.current?.contentWindow &&
        event.data?.type === 'quickchat_message_sent'
      ) {
        postHog?.capture(PostHogEventEnum.PreviewMessageSent, {
          author_type: event.data?.data?.author_type ?? 'visitor',
        });
      }
    };
    window.addEventListener('message', handlePreviewMessageSent);
    return () => window.removeEventListener('message', handlePreviewMessageSent);
  }, [postHog]);

  // preview_missed_reply_recovered: the widget posts quickchat_missed_reply_recovered when its
  // post-send fallback had to recover a reply the socket dropped, so this measures the blank-pane bug's real rate.
  useEffect(() => {
    const handleMissedReplyRecovered = (event: MessageEvent) => {
      if (
        event.source === localRef.current?.contentWindow &&
        event.data?.type === 'quickchat_missed_reply_recovered'
      ) {
        postHog?.capture(PostHogEventEnum.PreviewMissedReplyRecovered, {
          socket_connected: event.data?.data?.socket_connected ?? null,
        });
      }
    };
    window.addEventListener('message', handleMissedReplyRecovered);
    return () => window.removeEventListener('message', handleMissedReplyRecovered);
  }, [postHog]);

  // Forward the user's Advanced Settings metadata (prefixed METADATA_OVERRIDE_) to the
  // widget's quickchat_update_metadata channel, on load and on every edit.
  const previewMetadata = previewContext?.metadata;
  const prevMetadataKeysRef = useRef<Set<string>>(new Set());
  const sendMetadata = useCallback(
    (resetKeys: boolean) => {
      const win = localRef.current?.contentWindow;
      if (!win) return;
      if (resetKeys) prevMetadataKeysRef.current = new Set();
      const parsed = parseMetadataString(previewMetadata ?? '') ?? {};
      // Drop PreviewProvider auto-context (language is applied via ?language=, fullPathURL
      // is the dashboard URL) so it doesn't shadow the test language or add noise.
      const custom = Object.fromEntries(
        Object.entries(parsed).filter(([key]) => key !== 'language' && key !== 'fullPathURL'),
      );
      const { overrideMetadata, currentKeys } = transformMetadataForWidget(
        custom,
        prevMetadataKeysRef.current,
      );
      prevMetadataKeysRef.current = currentKeys;
      if (Object.keys(overrideMetadata).length === 0) return;
      win.postMessage(
        { type: 'quickchat_update_metadata', data: { metadata: overrideMetadata } },
        WIDGET_ORIGIN,
      );
    },
    [previewMetadata],
  );

  // Push on every metadata edit (widget already loaded).
  useEffect(() => {
    sendMetadata(false);
  }, [sendMetadata]);

  // On (re)load, reset key tracking and push once the widget's metadata listener exists.
  // That listener mounts with the theme-gated Module, so key off quickchat_ready_to_show
  // (fired after the theme resolves), not the earlier quickchat_ready.
  useEffect(() => {
    const onReady = (event: MessageEvent) => {
      if (
        event.source === localRef.current?.contentWindow &&
        event.data?.type === 'quickchat_ready_to_show'
      ) {
        sendMetadata(true);
      }
    };
    window.addEventListener('message', onReady);
    return () => window.removeEventListener('message', onReady);
  }, [sendMetadata]);

  // Stands in for chat.js: blur the screenshot behind the widget's glass and confirm it.
  const iframeInstanceKey = `${iframeKey}-${colorScheme ?? 'system'}-${language ?? ''}-${reloadToken}`;
  const [backdrop, setBackdrop] = useState<Backdrop | null>(null);
  const [backdropMotion, setBackdropMotion] = useState<BackdropMotion | null>(null);
  // Read by the message handler, which a motion must outlive.
  const backdropMotionRef = useRef<BackdropMotion | null>(null);
  const backdropRepliedRef = useRef(false);
  const playBackdropMotion = useCallback((motion: BackdropMotion | null) => {
    backdropMotionRef.current = motion;
    setBackdropMotion(motion);
  }, []);
  useEffect(() => {
    setBackdrop(null);
    playBackdropMotion(null);
    backdropRepliedRef.current = false;
  }, [iframeInstanceKey, playBackdropMotion]);
  const endBackdropMotion = useCallback(
    (ended: BackdropMotion) => {
      if (backdropMotionRef.current === ended) playBackdropMotion(null);
    },
    [playBackdropMotion],
  );

  useEffect(() => {
    const handleBackdrop = (event: MessageEvent) => {
      const win = localRef.current?.contentWindow;
      if (!win || event.source !== win || event.data?.type !== 'quickchat_backdrop') return;
      const blur = Number(event.data?.data?.blur);
      const shapes: unknown[] = Array.isArray(event.data?.data?.shapes)
        ? event.data.data.shapes
        : [];
      const validShapes = shapes.filter(isBackdropShape);
      const motion = isBackdropMotion(event.data?.data?.motion) ? event.data.data.motion : null;
      // A surface whose layout shifted off its handed-over path is followed per frame again.
      const endMotion = event.data?.data?.endMotion;
      if (typeof endMotion === 'string' && backdropMotionRef.current?.id === endMotion) {
        playBackdropMotion(null);
      }
      // Mid-motion the moving surface may not be measurable yet, but it is still being drawn.
      const moving = !!motion || !!backdropMotionRef.current;
      const wanted = Number.isFinite(blur) && blur > 0 && (validShapes.length > 0 || moving);
      // Unchanged frames keep the same state, so a settled window doesn't re-render the preview.
      const next = wanted ? { blur, shapes: validShapes } : null;
      setBackdrop((previous) => (isEqual(previous, next) ? previous : next));
      if (!wanted || motion) playBackdropMotion(wanted ? motion : null);
      // This browser's answer never changes, so only the widget's probe, and the first message, get one.
      if (!(blur > 0) || !backdropRepliedRef.current) {
        backdropRepliedRef.current = true;
        win.postMessage(
          { type: 'quickchat_backdrop_applied', data: { supported: supportsBackdropFilter() } },
          WIDGET_ORIGIN,
        );
      }
    };
    window.addEventListener('message', handleBackdrop);
    return () => window.removeEventListener('message', handleBackdrop);
  }, [playBackdropMotion]);

  // Observer only: the preview send is untouched, so their own Agent still answers it.
  usePreviewSupportIntent(localRef, detectSupportIntent && source === 'preview', onSupportOpened);

  // Drag is handled INSIDE the iframe (see widget's View.tsx — when `source=preview`
  // it applies the transform directly to the launcher element). Translating the whole
  // iframe here would move the entire fake page + background, not just the launcher.

  return (
    <div className={`flex flex-col items-center gap-4 w-full h-full ${className ?? ''}`}>
      {showViewModeToggle && (
        <RadioGroupSegmented
          value={viewMode}
          onValueChange={(value) => handleViewModeChange(value as ViewMode)}
        >
          {VIEW_MODE_OPTIONS.map(({ value, label, icon: Icon }) => (
            <RadioGroupSegmentedItem
              key={value}
              value={value}
              id={`view-mode-${value}`}
              className="flex items-center gap-1.5"
            >
              <Icon className="size-3.5" />
              {label}
            </RadioGroupSegmentedItem>
          ))}
        </RadioGroupSegmented>
      )}

      <div className="w-full flex-1 relative">
        {backdrop &&
          !isTransitioning &&
          Array.from(drawnShapes(backdrop.shapes, backdropMotion)).map(([key, shape]) => (
            <PreviewBackdropLayer
              key={key}
              shape={shape}
              blur={backdrop.blur}
              motion={backdropMotion?.id === key ? backdropMotion : null}
              onMotionEnd={endBackdropMotion}
            />
          ))}
        <iframe
          ref={mergedRef}
          data-quickchat-preview={source === 'preview' ? 'true' : undefined}
          // Combine the manual iframeKey (bumped on view-mode toggle) with
          // colorScheme + language so flipping the preview's light/dark switch or
          // the test language remounts the iframe and re-runs widget.html's
          // URL-param parsing (so the widget re-requests config in that language).
          key={iframeInstanceKey}
          src={src}
          className={cn(
            // relative keeps the iframe painted above the absolutely positioned backdrop layers.
            'relative w-full h-full border-0 bg-transparent transition-all duration-250 ease-in-out',
            fixedViewType ? 'rounded-none' : 'rounded-3xl',
            isTransitioning ? 'opacity-0 scale-[0.8]' : 'opacity-100 scale-100',
          )}
          // Fixed color-scheme so a transparent iframe body doesn't paint an opaque canvas hiding the screenshot.
          style={{ colorScheme: 'normal' }}
          allow="microphone"
        />
      </div>
    </div>
  );
};
export default NewWidget;
